import React, { useState, useEffect } from "react";
import styles from "./profile.module.css";
import PageTitle from "../../components/screens/page-title";
import Button from "../../components/ui/button";
import Input from "../../components/ui/input";
import Modal from "../../components/ui/modal";
import { useUser } from "../../contexts/user-context";
import { editUser, changePassword } from "../../services/apiService";
import { showSuccessToast, showErrorToast } from "../../utils/notifications";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../../hooks/usePageTitle";

const ProfilePage = () => {
  usePageTitle('Perfil');
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    username: "",
    nombre: "",
  });
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Get user ID from localStorage or context
  const usuarioId = localStorage.getItem("usuarioId") || user?.usuarioId;

  useEffect(() => {
    // Initialize profile data from localStorage or context
    const username =
      localStorage.getItem("username") ||
      localStorage.getItem("userName") ||
      user?.username ||
      "";
    const nombre = localStorage.getItem("userName") || user?.nombre || "";

    setProfileData({
      username,
      nombre,
    });
  }, [user]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async () => {
    if (!usuarioId) {
      showErrorToast("No se pudo obtener el ID del usuario");
      return;
    }

    setIsLoading(true);

    try {
      await editUser(usuarioId, {
        username: profileData.username,
        nombre: profileData.nombre,
      });

      // Update localStorage with new data
      localStorage.setItem("username", profileData.username);
      localStorage.setItem("userName", profileData.nombre);

      showSuccessToast("Perfil actualizado correctamente");
    } catch (error) {
      console.error("Error updating profile:", error);
      showErrorToast("Error al actualizar el perfil");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorToast("Las contraseñas nuevas no coinciden");
      return;
    }

    if (!passwordData.oldPassword || !passwordData.newPassword) {
      showErrorToast("Todos los campos de contraseña son obligatorios");
      return;
    }

    setIsLoading(true);

    try {
      await changePassword({
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });

      showSuccessToast("Contraseña cambiada correctamente");
      setIsPasswordModalOpen(false);
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error changing password:", error);
      showErrorToast(
        "Error al cambiar la contraseña. Verifique su contraseña actual."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className={styles.container}>
      <PageTitle iconName="user" title="Perfil de Usuario" />

      <div className={styles.profileSection}>
        <h3 className={styles.sectionTitle}>Información Personal</h3>

        <div className={styles.formGroup}>
          <Input
            label="Nombre de Usuario"
            name="username"
            value={profileData.username}
            onChange={handleProfileChange}
            placeholder="Nombre de usuario"
            style={{ width: "100%" }}
          />
        </div>

        <div className={styles.formGroup}>
          <Input
            label="Nombre Completo"
            name="nombre"
            value={profileData.nombre}
            onChange={handleProfileChange}
            placeholder="Nombre completo"
            style={{ width: "100%" }}
          />
        </div>

        <div className={styles.buttonGroup}>
          <Button onClick={handleSaveProfile} disabled={isLoading}>
            {isLoading ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>
      </div>

      <div className={styles.securitySection}>
        <h3 className={styles.sectionTitle}>Seguridad</h3>

        <div className={styles.buttonGroup}>
          <Button
            onClick={() => setIsPasswordModalOpen(true)}
            variant="secondary"
          >
            Cambiar Contraseña
          </Button>
        </div>
      </div>

      <div className={styles.buttonGroup}>
        <Button onClick={handleLogout} variant="danger">
          Cerrar Sesión
        </Button>
      </div>

      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setPasswordData({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        }}
        title="Cambiar Contraseña"
      >
        <div className={styles.passwordForm}>
          <Input
            label="Contraseña Actual"
            type="password"
            name="oldPassword"
            value={passwordData.oldPassword}
            onChange={handlePasswordChange}
            placeholder="Contraseña actual"
            style={{ width: "100%" }}
          />

          <Input
            label="Nueva Contraseña"
            type="password"
            name="newPassword"
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
            placeholder="Nueva contraseña"
            style={{ width: "100%" }}
          />

          <Input
            label="Confirmar Nueva Contraseña"
            type="password"
            name="confirmPassword"
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange}
            placeholder="Confirmar nueva contraseña"
            style={{ width: "100%" }}
          />

          <div className={styles.modalButtons}>
            <Button onClick={handleChangePassword} disabled={isLoading}>
              {isLoading ? "Cambiando..." : "Cambiar Contraseña"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setIsPasswordModalOpen(false);
                setPasswordData({
                  oldPassword: "",
                  newPassword: "",
                  confirmPassword: "",
                });
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProfilePage;
